import { createRouter, createRoute, redirect, ParsedLocation, lazyRouteComponent } from "@tanstack/react-router";
import LoginPage from "../pages/LoginPage/LoginPage";
import { Dashboard, RootRoute } from "./RootRoute";
import { AuthType } from "../context/AuthContext";
import LoginTenantPage from "../pages/LoginTenantPage/LoginTenantPage";
import HomePage from "../pages/HomePage/HomePage";
import CreateApplicationPage from "../pages/CreateApplicationPage/CreateApplicationPage";
import TenantPublicPage from "../pages/TenantPublicPage/TenantPublicPage";
import { setNavigate } from "./navigation";

// Code-splitting per trasa (lazyRouteComponent = React.lazy + preload,
// router sam owija w Suspense z defaultPendingComponent). Eager zostaje
// tylko główna ścieżka mieszkańca: HomePage, TenantPublicPage (+ mapa),
// CreateApplicationPage i strony logowania. Strony admina, pomocy
// i one-shot (confirm/cancel/activate z linków mailowych) ładują się
// osobnymi chunkami przy pierwszym wejściu — nie obciążają publicznego
// bundla (m.in. react-markdown z HelpPage, @dnd-kit z ConsultationAdmin).
const ProceduresPage = lazyRouteComponent(() => import("../pages/ProceduresPage/ProceduresPage"));
const RegisterUserPage = lazyRouteComponent(() => import("../pages/RegisterUserPage/RegisterUserPage"));
const CreateTenantPage = lazyRouteComponent(() => import("../pages/CreateTenantPage/CreateTenantPage"));
const ActivateAdminAccountPage = lazyRouteComponent(() => import("../pages/ActivateAdminAccountPage/ActivateAdminAccountPage"));
const RegisterEmailPage = lazyRouteComponent(() => import("../pages/RegisterEmailPage/RegisterEmailPage"));
const TenantListPage = lazyRouteComponent(() => import("../pages/TenantListPage/TenantListPage"));
const EditTenantPage = lazyRouteComponent(() => import("../pages/EditTenantPage/EditTenantPage"));
const CreateProcedurePage = lazyRouteComponent(() => import("../pages/CreateProcedurePage/CreateProcedurePage"));
const EditUserPage = lazyRouteComponent(() => import("../pages/EditUserPage/EditUserPage"));
const ConfirmAppointmentPage = lazyRouteComponent(() => import("../pages/ConfirmAppointmentPage/ConfirmAppointmentPage"));
const ConfirmApplicationPage = lazyRouteComponent(() => import("../pages/ConfirmApplicationPage/ConfirmApplicationPage"));
const CancelAppointmentPage = lazyRouteComponent(() => import("../pages/CancelAppointmentPage/CancelAppointmentPage"));
const ConsultationAdminPage = lazyRouteComponent(() => import("../pages/ConsultationAdminPage/ConsultationAdminPage"));
const ConsultantDashboardPage = lazyRouteComponent(() => import("../pages/ConsultantDashboardPage/ConsultantDashboardPage"));
const ManageConsultantsPage = lazyRouteComponent(() => import("../pages/ManageConsultantsPage/ManageConsultantsPage"));
const ApplicationsListPage = lazyRouteComponent(() => import("../pages/ApplicationsListPage/ApplicationsListPage"));
const DashboardPage = lazyRouteComponent(() => import("../pages/DashboardPage/DashboardPage"));
const ApplicationsMapPage = lazyRouteComponent(() => import("../pages/ApplicationsMapPage/ApplicationsMapPage"));
const ConsultantMapPage = lazyRouteComponent(() => import("../pages/ConsultantMapPage/ConsultantMapPage"));
const ConsultantSearchLogPage = lazyRouteComponent(() => import("../pages/ConsultantSearchLogPage/ConsultantSearchLogPage"));
const SuperadminsPage = lazyRouteComponent(() => import("../pages/SuperadminsPage/SuperadminsPage"));
const EditProcedurePage = lazyRouteComponent(() => import("../pages/EditProcedurePage/EditProcedurePage"));
const HelpPage = lazyRouteComponent(() => import("../pages/HelpPage/HelpPage"));
const HelpAdminPage = lazyRouteComponent(() => import("../pages/HelpAdminPage/HelpAdminPage"));
const SurveyPage = lazyRouteComponent(() => import("../pages/SurveyPage/SurveyPage"));
const SurveyResultsPage = lazyRouteComponent(() => import("../pages/SurveyResultsPage/SurveyResultsPage"));

const protectRoute = ({ context, location }: { context: AuthType; location: ParsedLocation }) => {
  const mainTenant = localStorage.getItem("tenant:mainTenant");
  const redirectTo = mainTenant ? `/${JSON.parse(mainTenant)}/login` : "/admin/login";
  if (!context.isLoggedIn) {
    throw redirect({
      to: redirectTo,
      search: {
        redirect: location.href,
      },
    });
  }
};
export const publicRoute = createRoute({
  getParentRoute: () => RootRoute,
  path: "/",
  component: HomePage,
});

const adminLogin = createRoute({
  getParentRoute: () => RootRoute,
  path: "/admin/login",
  component: LoginPage,
});

const protectedRoute = createRoute({
  getParentRoute: () => RootRoute,
  id: "protected",
  component: Dashboard,
});

export const loginTenantRoute = createRoute({
  getParentRoute: () => RootRoute,
  path: "/$tenant/login",
  component: LoginTenantPage,
});

export const procedureListRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/$tenant/procedures",
  component: ProceduresPage,
});

export const tenantPublicRoute = createRoute({
  getParentRoute: () => RootRoute,
  path: "/$tenant",
  component: TenantPublicPage,
});

/**
 * Dedykowana strona konkretnej procedury gminy.
 * URL: /{tenant}/{procedureSlug} gdzie slug = Procedure.name.
 * Statyczne sciezki (np. /$tenant/login, /$tenant/edit) maja priorytet,
 * wiec nazwy zarezerwowane sa blokowane w schemacie.
 */
export const tenantProcedurePublicRoute = createRoute({
  getParentRoute: () => RootRoute,
  path: "/$tenant/$procedureSlug",
  component: TenantPublicPage,
});

export const confirmAppointmentRoute = createRoute({
  getParentRoute: () => RootRoute,
  path: "/$tenant/consultation/confirm/$appointmentId/$confirmationKey",
  component: ConfirmAppointmentPage,
});

export const cancelAppointmentRoute = createRoute({
  getParentRoute: () => RootRoute,
  path: "/$tenant/consultation/cancel/$appointmentId/$confirmationKey",
  component: CancelAppointmentPage,
});

export const confirmApplicationRoute = createRoute({
  getParentRoute: () => RootRoute,
  path: "/$tenant/application/confirm/$applicationId/$confirmationKey",
  component: ConfirmApplicationPage,
});

export const registerUserRoute = createRoute({
  getParentRoute: () => RootRoute,
  path: "/$tenant/register",
  component: RegisterUserPage,
});

export const createTenantRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/admin/create-tenant",
  component: CreateTenantPage,
  beforeLoad: protectRoute,
});

export const activateAdminAccountRoute = createRoute({
  getParentRoute: () => RootRoute,
  path: "/$tenant/user/$userId/activate-account/$confirmationKey",
  component: ActivateAdminAccountPage,
});
export const registerEmailRoute = createRoute({
  getParentRoute: () => RootRoute,
  path: "/$tenant/user/$userId/register-email/$confirmationKey",
  component: RegisterEmailPage,
});
export const tenantListRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/admin/tenant",
  component: TenantListPage,
  beforeLoad: protectRoute,
});
export const editTenantRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/$tenant/edit",
  component: EditTenantPage,
  beforeLoad: protectRoute,
});

export const createProcedureRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/$tenant/create-procedure",
  component: CreateProcedurePage,
  beforeLoad: protectRoute,
});

export const editProcedureRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/$tenant/edit-procedure/$formId",
  component: EditProcedurePage,
  beforeLoad: protectRoute,
});

export const editUserRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/$tenant/edit-me",
  component: EditUserPage,
  beforeLoad: protectRoute,
});

export const consultationAdminRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/$tenant/consultation-admin",
  component: ConsultationAdminPage,
  beforeLoad: protectRoute,
});

export const consultantDashboardRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/$tenant/consultant-dashboard",
  component: ConsultantDashboardPage,
  beforeLoad: protectRoute,
});

export const dashboardRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/$tenant/dashboard",
  component: DashboardPage,
  beforeLoad: protectRoute,
});

export const applicationsMapRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/$tenant/applications-map",
  component: ApplicationsMapPage,
  beforeLoad: protectRoute,
});

export const consultantMapRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/$tenant/consultant-map",
  component: ConsultantMapPage,
  beforeLoad: protectRoute,
});

export const consultantSearchLogRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/$tenant/consultant-search-log",
  component: ConsultantSearchLogPage,
  beforeLoad: protectRoute,
});

export const applicationsListRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/$tenant/applications",
  component: ApplicationsListPage,
  beforeLoad: protectRoute,
});

export const superadminsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/admin/superadmins",
  component: SuperadminsPage,
  beforeLoad: protectRoute,
});

export const manageConsultantsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/$tenant/manage-consultants",
  component: ManageConsultantsPage,
  beforeLoad: protectRoute,
});

export const createApplicationRoute = createRoute({
  getParentRoute: () => RootRoute,
  path: "/$tenant/$formId/application",
  component: CreateApplicationPage,
});

/** Geoankieta — publiczna, bez logowania (art. 8i ust. 1 pkt 4 u.p.z.p.). */
export const surveyRoute = createRoute({
  getParentRoute: () => RootRoute,
  path: "/$tenant/$formId/geoankieta",
  component: SurveyPage,
});

/** Wyniki geoankiety — admin / konsultant / superadmin. */
export const surveyResultsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/$tenant/survey-results/$formId",
  component: SurveyResultsPage,
  beforeLoad: protectRoute,
});

/** Pełna strona pomocy — globalna (/help) i per gmina (/$tenant/help).
 *  Obie publiczne — bez logowania. Backend zwraca tylko artykuły
 *  audience='public' dla niezalogowanych. */
export const helpGlobalRoute = createRoute({
  getParentRoute: () => RootRoute,
  path: "/help",
  component: HelpPage,
});

export const helpTenantRoute = createRoute({
  getParentRoute: () => RootRoute,
  path: "/$tenant/help",
  component: HelpPage,
});

/** Edytor artykułów pomocy. /admin/help-articles dla superadmina (globalne),
 *  /$tenant/help-articles dla admina gminy (lokalne nadpisania). Oba protected. */
export const helpAdminGlobalRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/admin/help-articles",
  component: HelpAdminPage,
  beforeLoad: protectRoute,
});

export const helpAdminTenantRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: "/$tenant/help-articles",
  component: HelpAdminPage,
  beforeLoad: protectRoute,
});

const routeTree = RootRoute.addChildren([
  protectedRoute.addChildren([
    createProcedureRoute,
    editTenantRoute,
    editUserRoute,
    procedureListRoute,
    createTenantRoute,
    tenantListRoute,
    consultationAdminRoute,
    consultantDashboardRoute,
    manageConsultantsRoute,
    dashboardRoute,
    applicationsListRoute,
    applicationsMapRoute,
    consultantMapRoute,
    consultantSearchLogRoute,
    superadminsRoute,
    editProcedureRoute,
    helpAdminGlobalRoute,
    helpAdminTenantRoute,
    surveyResultsRoute,
  ]),
  publicRoute.addChildren([
    loginTenantRoute,
    activateAdminAccountRoute,
    registerEmailRoute,
    registerUserRoute,
    confirmAppointmentRoute,
    cancelAppointmentRoute,
    confirmApplicationRoute,
    createApplicationRoute,
    surveyRoute,
    helpGlobalRoute,
    helpTenantRoute,
    tenantPublicRoute,
    tenantProcedurePublicRoute,
    adminLogin,
  ]),
]);
// Kontekst routera (AuthType) ustawiany jest przez RouterProvider w main.tsx
// (`<RouterProvider router={router} context={{ ...authState }} />`). Tu podajemy
// pusty placeholder, żeby satysfakcjonowac wymóg `context` w typie.
export const router = createRouter({
  routeTree,
  context: {} as AuthType,
  // Fallback na czas ładowania chunka lazy trasy (zwykle <100 ms z tego
  // samego origina; widoczny dopiero po defaultPendingMs).
  defaultPendingComponent: () => (
    <div className="p-8 text-center text-gray-500">Ładowanie…</div>
  ),
});

setNavigate(router.navigate);
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
